import { TimelineEntry, TimelineStatus } from '~/app/pages/Transfers/_components/Timeline';
import { TransferCompany } from '~/app/pages/Transfers/_components/Timeline/types';
import { buildTimelineEntryForTransferReviews, buildTransferParty } from '~/app/pages/Transfers/_utils/timeline';
import { TransferStatus } from '~/lib/transfers';
import { AchReturn, AchTransfer, Counterparty } from '~/repositories';
import { AccountNumber, BankAccount } from '~/repositories/BankAccountRepository';
import { ACHTransferBatch } from '~/typings/API';
import { formatString } from '~/util';

const achEvents = [
  {
    label: 'Created',
    field: 'createdAt',
  },
  {
    label: 'Pending Approval',
    field: 'preReviewAt',
  },
  {
    label: 'Submitted',
    field: 'submittedAt',
  },
  {
    label: 'Acknowledged',
    field: 'acknowledgedAt',
  },
  {
    label: 'Processed',
    field: 'processedAt',
  },
  {
    label: 'Settled',
    field: 'settledAt',
    status: TimelineStatus.Success,
  },
  {
    label: 'Rejected',
    field: 'rejectedAt',
    status: TimelineStatus.Danger,
  },
  {
    label: 'Canceled',
    field: 'cancelledAt',
    status: TimelineStatus.Danger,
  },
  {
    label: 'Returned',
    field: 'returnedAt',
    status: TimelineStatus.Returned,
  },
  {
    label: 'Completed',
    field: 'completedAt',
    status: TimelineStatus.Success,
  },
];

export const buildAchEvents = ({
  transfer,
  account,
  counterparty,
  company,
  achBatch,
}: {
  transfer: AchTransfer;
  account: {
    accountNumber: AccountNumber;
    bankAccount: BankAccount;
    entityType?: 'person' | 'business';
  };
  counterparty?: Counterparty;
  company?: TransferCompany;
  achBatch?: ACHTransferBatch;
}): TimelineEntry[] => {
  const rejected = (transfer.status.toLowerCase() as TransferStatus) === TransferStatus.CANCELED;
  return achEvents
    .filter(
      (event) =>
        (event.field === 'createdAt' ||
          (!rejected && !transfer.returnedAt && event.field === 'settledAt') ||
          !!transfer[event.field as keyof typeof transfer]) &&
        event.field !== 'returnedAt'
    )
    .map((event) => {
      if (event.field === 'preReviewAt') {
        return buildTimelineEntryForTransferReviews(
          transfer[event.field as keyof typeof transfer] as string | undefined,
          transfer.reviews
        );
      }

      const date = (transfer[event.field as keyof typeof transfer] as string | undefined)
        ? new Date(transfer[event.field as keyof typeof transfer] as string)
        : undefined;

      let amount;
      if (transfer.amount) {
        amount = {
          value: transfer.amount,
          isAdding: event.field === 'completedAt' || event.field === 'settledAt',
          currencyCode: 'USD',
        };
      }

      const party =
        (event.field === 'createdAt' ||
          (event.field === 'completedAt' && transfer.settledAt) ||
          (event.field === 'settledAt' && !transfer.completedAt) ||
          event.field === 'cancelledAt') &&
        (counterparty || company || account)
          ? buildTransferParty({
              account: {
                ...account,
                transferType: transfer.type,
              },
              counterparty,
              company,
              isIncoming: transfer.type === 'CREDIT' ? transfer.isIncoming : !transfer.isIncoming,
              isOriginator: event.field === 'createdAt',
            })
          : undefined;

      const defaultStatus = rejected ? TimelineStatus.Danger : TimelineStatus.Pending;
      const status = !!transfer[event.field as keyof typeof transfer] ? event.status : defaultStatus;

      return {
        label: event.label,
        amount,
        date,
        partyData: party,
        status,
        achBatch: event.field === 'createdAt' ? achBatch : undefined,
      };
    });
};

export const buildAchReturnEvents = ({
  achReturn,
  transfer,
  account,
  counterparty,
  company,
}: {
  achReturn?: DeepPartial<AchReturn>;
  transfer: AchTransfer;
  account: {
    accountNumber: AccountNumber;
    bankAccount: BankAccount;
    entityType?: 'person' | 'business';
  };
  counterparty?: Counterparty;
  company?: TransferCompany;
}): TimelineEntry[] => {
  if (!achReturn?.details) return [];

  return achReturn.details.map((detail, index) => {
    // The return code is identical across every lifecycle detail (INITIATED, SENT, ...), so only
    // surface it when it actually changes from the prior step to avoid rendering the same code card repeatedly.
    const previousCode = index > 0 ? achReturn.details?.[index - 1]?.returnCode : undefined;
    const showCode = detail.returnCode !== previousCode;

    return {
      label: `Return ${formatString(detail.status)}`,
      date: new Date(detail.createdAt),
      status: detail.status === 'INITIATED' ? TimelineStatus.Returned : TimelineStatus.Success,
      partyData:
        !transfer.settledAt && index === 0
          ? buildTransferParty({
              account: {
                ...account,
                transferType: transfer.type,
              },
              counterparty,
              company,
              isIncoming: transfer.type === 'CREDIT' ? transfer.isIncoming : !transfer.isIncoming,
              isOriginator: false,
            })
          : undefined,
      returnData: {
        addenda: detail.addenda,
        code: showCode ? detail.returnCode : undefined,
        description: detail.description,
      },
    } as TimelineEntry;
  });
};
